import { useState } from 'react';
import { Check, Minus, Plus, Undo2, AlertTriangle, ArrowRightLeft, ScanLine } from 'lucide-react';
import BarcodeScanner from '../../shared/BarcodeScanner';
import { parseLotScan, scanMatches } from '../../../utils/lotScan';
import { slotWhere } from '../../../utils/nurseryRowSlots';
import Button from '../../ui/Button';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { formatLocalDateInTz } from '../../../utils/timezone';
import { cn } from '../../../utils/cn';
import { TagSwatch } from '../TagSwatch';
import {
  SHORT_REASON_LABELS,
  type PickAlternative,
  type PickItem,
  type PickList,
  type PickShortReason,
} from '../../../types/nursery';

/**
 * One allocation on a pick list, built for a phone in a paddock: what to
 * pick, where, for whom, and one big button when it is all there.
 *
 * Fewer than allocated opens the shortfall form in place — how many, why, and
 * optionally the lot's real count or a later ready date. A short item then
 * offers to take the rest from another ready lot at the despatch farm.
 * Every one of those is recorded server-side; nothing here is guesswork.
 */

export interface PickItemCardProps {
  item: PickItem;
  loadId: number;
  despatchFarmName: string;
  timezone: string;
  /** False once the load can no longer be picked. */
  editable: boolean;
  onChanged: (list: PickList) => void;
  /** A scanned label found this lot. */
  highlighted?: boolean;
}

const REASONS = Object.keys(SHORT_REASON_LABELS) as PickShortReason[];

const inputCls = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

export function PickItemCard({ item, loadId, despatchFarmName, timezone, editable, onChanged, highlighted }: PickItemCardProps) {
  const [scanning, setScanning] = useState(false);
  const [mode, setMode] = useState<'idle' | 'short' | 'substitute'>('idle');
  const [qty, setQty] = useState(item.quantity);
  const [reason, setReason] = useState<PickShortReason | ''>('');
  const [notes, setNotes] = useState('');
  const [fixCount, setFixCount] = useState(false);
  const [counted, setCounted] = useState<number | ''>('');
  const [readyDate, setReadyDate] = useState('');
  const [alternatives, setAlternatives] = useState<PickAlternative[] | null>(null);
  const [altLot, setAltLot] = useState<number | ''>('');
  const [altQty, setAltQty] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = item.pick;
  const shortfall = pick ? item.quantity - pick.quantity_picked : 0;
  const where = slotWhere(item);

  const run = async (fn: () => Promise<{ success: boolean; data?: PickList } & object>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      if (res.success && res.data) {
        onChanged(res.data);
        return true;
      }
      setError(errorMessage(res, fallback));
      return false;
    } catch (e) {
      setError(errorMessage(e, fallback));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const pickAll = () => run(() => nurseryService.recordPick(item.allocation_id, { quantity: item.quantity }), 'Could not record the pick');

  const saveShort = async () => {
    if (qty < item.quantity && !reason) {
      setError('Choose why fewer were picked.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (fixCount && counted !== '') {
        await nurseryService.correctLotCount(item.lot_id, {
          counted: Number(counted), new_ready_date: readyDate || null,
          reason: reason ? SHORT_REASON_LABELS[reason] : null, load_id: loadId,
        });
      } else if (reason === 'not_ready' && readyDate) {
        await nurseryService.correctLotCount(item.lot_id, {
          counted: item.lot_count, new_ready_date: readyDate, reason: SHORT_REASON_LABELS.not_ready, load_id: loadId,
        });
      }
    } catch (e) {
      setBusy(false);
      setError(errorMessage(e, 'Could not correct the lot'));
      return;
    }
    setBusy(false);
    const ok = await run(() => nurseryService.recordPick(item.allocation_id, {
      quantity: qty, short_reason: qty < item.quantity ? (reason || null) : null, notes: notes || null,
    }), 'Could not record the pick');
    if (ok) setMode('idle');
  };

  const openSubstitute = async () => {
    setMode('substitute');
    setAlternatives(null);
    setAltLot('');
    setAltQty('');
    setError(null);
    try {
      const res = await nurseryService.getPickAlternatives(item.allocation_id);
      if (res.success) setAlternatives(res.data);
      else { setAlternatives([]); setError(errorMessage(res, 'Could not find other lots')); }
    } catch (e) {
      setAlternatives([]);
      setError(errorMessage(e, 'Could not find other lots'));
    }
  };

  const saveSubstitute = async () => {
    if (!altLot || !altQty) return;
    const ok = await run(() => nurseryService.substituteLot(item.allocation_id, altLot, Number(altQty)), 'Could not take them from that lot');
    if (ok) setMode('idle');
  };

  return (
    <li id={`pick-${item.allocation_id}`} className={cn('rounded-xl border bg-white p-4', highlighted && 'ring-2 ring-primary-500',
      pick?.status === 'picked' ? 'border-green-200 bg-green-50/40'
        : pick?.status === 'short' ? 'border-amber-200 bg-amber-50/40'
          : !item.at_despatch_farm ? 'border-neutral-200 bg-neutral-50' : 'border-neutral-200')}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-neutral-900 break-words">
            {item.cultivar_name}{item.rootstock_name ? ` / ${item.rootstock_name}` : ''}
          </p>
          <p className="text-sm text-neutral-600 break-words">{item.tree_type_name}{item.lot_code ? ` · Lot ${item.lot_code}` : ''}</p>
          {item.sold_as && (
            <p className="mt-0.5 flex items-center gap-1.5 text-xs text-neutral-600 break-words">
              <TagSwatch hex={item.tag_colour_hex} name={item.tag_colour_name} />
              <span>
                Sold as {item.sold_as}
                {item.tag_colour_name ? ` · ${item.tag_colour_name} tag` : ''}
              </span>
            </p>
          )}
          <p className="text-sm text-neutral-800 mt-1 break-words">{where}</p>
          <p className="text-xs text-neutral-500 mt-1 break-words">
            {item.order_number} · {item.customer_name}
            {item.substituted_from_allocation_id ? ' · taken from another lot' : ''}
          </p>
          {/* What the order asks the picker to do: a sticker, a comment from the purchase order. */}
          {item.line_notes && (
            <p className="mt-1 rounded bg-blue-50 border border-blue-200 px-2 py-1 text-sm text-blue-900 break-words">
              {item.line_notes}
            </p>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-2xl font-bold text-neutral-900 leading-none">{item.quantity}</p>
          <p className="text-xs text-neutral-500">to pick</p>
        </div>
      </div>

      {!item.at_despatch_farm && (
        <p className="mt-3 flex items-start gap-2 text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>These are at {item.farm_name ?? 'no known farm'}. They have to be moved to {despatchFarmName} before they can be picked.</span>
        </p>
      )}

      {pick && mode === 'idle' && (
        <div className="mt-3 text-sm">
          {pick.status === 'picked' ? (
            <p className="flex items-center gap-1.5 text-green-800 font-medium">
              <Check className="h-4 w-4" /> Picked {pick.quantity_picked}
              {pick.picked_by_name ? <span className="font-normal text-neutral-500"> by {pick.picked_by_name}</span> : null}
            </p>
          ) : (
            <p className="text-amber-800">
              <span className="font-medium">Short: {pick.quantity_picked} of {item.quantity}.</span>{' '}
              {pick.short_reason ? SHORT_REASON_LABELS[pick.short_reason] : ''}
              {pick.notes ? ` · ${pick.notes}` : ''}
            </p>
          )}
        </div>
      )}

      {error && <p className="mt-3 text-sm text-red-700">{error}</p>}

      {editable && item.at_despatch_farm && mode === 'idle' && (
        <div className="mt-3 flex flex-wrap gap-2">
          {!pick && (
            <>
              <Button size="md" onClick={pickAll} disabled={busy} className="flex-1 min-w-[10rem] justify-center">
                <Check className="h-5 w-5" /> Picked all {item.quantity}
              </Button>
              <Button variant="outline" size="md" disabled={busy}
                onClick={() => { setQty(item.quantity); setReason(''); setMode('short'); }}>
                Fewer…
              </Button>
            </>
          )}
          {pick && pick.status === 'short' && shortfall > 0 && (
            <Button size="sm" onClick={openSubstitute} disabled={busy}>
              <ArrowRightLeft className="h-4 w-4" /> Take {shortfall} from another lot
            </Button>
          )}
          {pick && (
            <>
              <Button variant="outline" size="sm" disabled={busy}
                onClick={() => { setQty(pick.quantity_picked); setReason(pick.short_reason ?? ''); setNotes(pick.notes ?? ''); setMode('short'); }}>
                Change
              </Button>
              <Button variant="outline" size="sm" disabled={busy}
                onClick={() => run(() => nurseryService.undoPick(item.allocation_id), 'Could not undo the pick')}>
                <Undo2 className="h-4 w-4" /> Undo
              </Button>
            </>
          )}
        </div>
      )}

      {mode === 'short' && (
        <div className="mt-4 space-y-3 border-t border-neutral-100 pt-3">
          <div>
            <span className="block text-sm font-medium text-neutral-700 mb-1">How many did you pick?</span>
            <div className="flex items-center gap-2">
              <button type="button" aria-label="One fewer" onClick={() => setQty((q) => Math.max(0, q - 1))}
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-neutral-300"><Minus className="h-4 w-4" /></button>
              <input type="number" inputMode="numeric" min={0} max={item.quantity} value={qty} aria-label="Quantity picked"
                onChange={(e) => setQty(Math.max(0, Math.min(item.quantity, Number(e.target.value) || 0)))}
                className="w-20 border border-neutral-300 rounded-lg px-2 py-2 text-center text-lg font-semibold" />
              <button type="button" aria-label="One more" onClick={() => setQty((q) => Math.min(item.quantity, q + 1))}
                className="h-10 w-10 flex items-center justify-center rounded-lg border border-neutral-300"><Plus className="h-4 w-4" /></button>
              <span className="text-sm text-neutral-500">of {item.quantity}</span>
            </div>
          </div>

          {qty < item.quantity && (
            <fieldset>
              <legend className="block text-sm font-medium text-neutral-700 mb-1">Why?</legend>
              <div className="space-y-1">
                {REASONS.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm text-neutral-700">
                    <input type="radio" name={`reason-${item.allocation_id}`} checked={reason === r} onChange={() => setReason(r)} />
                    {SHORT_REASON_LABELS[r]}
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {reason === 'not_ready' && !fixCount && (
            <div>
              <label htmlFor={`ready-${item.allocation_id}`} className="block text-sm font-medium text-neutral-700 mb-1">
                When will they be ready? (optional)
              </label>
              <input id={`ready-${item.allocation_id}`} type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} className={inputCls} />
            </div>
          )}

          <label className="flex items-start gap-2 text-sm text-neutral-700">
            <input type="checkbox" className="mt-0.5" checked={fixCount} onChange={(e) => setFixCount(e.target.checked)} />
            <span>The lot's count is wrong. It says {item.lot_count}.</span>
          </label>
          {fixCount && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label htmlFor={`count-${item.allocation_id}`} className="block text-sm font-medium text-neutral-700 mb-1">
                  How many are really in the lot now?
                </label>
                <input id={`count-${item.allocation_id}`} type="number" inputMode="numeric" min={0} value={counted}
                  onChange={(e) => setCounted(e.target.value === '' ? '' : Math.max(0, Number(e.target.value)))} className={inputCls} />
              </div>
              <div>
                <label htmlFor={`ready2-${item.allocation_id}`} className="block text-sm font-medium text-neutral-700 mb-1">
                  New ready date (optional)
                </label>
                <input id={`ready2-${item.allocation_id}`} type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)} className={inputCls} />
              </div>
            </div>
          )}

          <div>
            <label htmlFor={`notes-${item.allocation_id}`} className="block text-sm font-medium text-neutral-700 mb-1">Notes (optional)</label>
            <input id={`notes-${item.allocation_id}`} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setMode('idle'); setError(null); }} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={saveShort} disabled={busy}>{busy ? 'Saving…' : `Record ${qty} picked`}</Button>
          </div>
        </div>
      )}

      {mode === 'substitute' && (
        <div className="mt-4 space-y-3 border-t border-neutral-100 pt-3">
          <p className="text-sm font-medium text-neutral-700">Take {shortfall} from another lot of the same tree</p>
          {alternatives === null ? (
            <p className="text-sm text-neutral-500">Looking for ready lots at {despatchFarmName}…</p>
          ) : alternatives.length === 0 ? (
            <p className="text-sm text-amber-800">
              No other ready lot {item.sold_as ? `that can be sold as ${item.sold_as}` : 'of this tree'} at {despatchFarmName} has any free. The rest stays short and goes on a backorder when the load is despatched.
            </p>
          ) : (
            <ul className="space-y-2" role="radiogroup" aria-label="Lots to take them from">
              {alternatives.map((a) => (
                <li key={a.lot_id}>
                  <label className="flex items-start gap-2 rounded-lg border border-neutral-200 p-3 text-sm">
                    <input type="radio" name={`alt-${item.allocation_id}`} checked={altLot === a.lot_id} className="mt-0.5"
                      onChange={() => { setAltLot(a.lot_id); setAltQty(Math.min(shortfall, a.available)); }} />
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-neutral-900">{a.lot_code ? `Lot ${a.lot_code}` : `Lot #${a.lot_id}`}</span>
                      {item.sold_as && a.cultivar_name && (
                        <span className="block text-neutral-800 break-words">
                          {[a.cultivar_name, a.rootstock_name].filter(Boolean).join(' / ')}
                        </span>
                      )}
                      <span className="block text-neutral-600 break-words">
                        {slotWhere(a)}
                      </span>
                      {a.ready_date && <span className="block text-xs text-neutral-500">Ready {formatLocalDateInTz(a.ready_date, timezone)}</span>}
                    </span>
                    <span className="text-right flex-shrink-0">
                      <span className="block font-semibold">{a.available}</span>
                      <span className="text-xs text-neutral-500">free</span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {alternatives && alternatives.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setScanning(true)}>
              <ScanLine className="h-4 w-4" /> Scan the lot you are taking them from
            </Button>
          )}
          {altLot !== '' && (
            <div>
              <label htmlFor={`altqty-${item.allocation_id}`} className="block text-sm font-medium text-neutral-700 mb-1">How many from this lot?</label>
              <input id={`altqty-${item.allocation_id}`} type="number" inputMode="numeric" min={1} value={altQty}
                onChange={(e) => setAltQty(e.target.value === '' ? '' : Math.max(1, Number(e.target.value)))} className={inputCls} />
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setMode('idle'); setError(null); }} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={saveSubstitute} disabled={busy || !altLot || !altQty}>Add to the pick list</Button>
          </div>
        </div>
      )}
      {scanning && (
        <BarcodeScanner
          label="Scan the lot label"
          onClose={() => setScanning(false)}
          onScan={(raw) => {
            setScanning(false);
            const scan = parseLotScan(raw);
            const alt = scan && (alternatives ?? []).find((a) => scanMatches(scan, { id: a.lot_id, code: a.lot_code }));
            if (alt) {
              setAltLot(alt.lot_id);
              setAltQty(Math.min(shortfall, alt.available));
              setError(null);
            } else {
              setError(item.lot_id != null && scan && scanMatches(scan, { id: item.lot_id, code: item.lot_code })
                ? 'That is the lot this item is already allocated from.'
                : 'That lot is not one of the ready lots of this tree with trees free.');
            }
          }}
        />
      )}
    </li>
  );
}

export default PickItemCard;
